import { ChangeEvent, useEffect, useState } from 'react';
import { DynamicSearchBar } from '../../../components/DynamicSearchBar';
import { SearchBy } from '../../../types/commonTypes';
import { DeviceList } from './DeviceList';
import { ReviewItems } from './ReviewItems';
import { Label } from '../../../components/Label';
import { Input } from '../../../components/Input';

export const Devices = (props: Omit<CreateAccountDevicesProps, 'onChange'>) => {
  const {
    asReview,
    formValues,
    inventory = [],
    loading = false,
    updateFormState,
  } = props;
  const [location, setLocation] = useState('');
  const [searchBy, setSearchBy] = useState<SearchBy>('GUID');
  const [searchOptions, setSearchOptions] = useState<string[]>(
    inventory.map((device) => device.id)
  );

  useEffect(() => {
    if (!loading) {
      handleSearchByChange(searchBy);
    }
  }, [loading, inventory.length]);

  const handleSearch = (value: InventoryDevice, searchBy: SearchBy) => {
    const device = inventory.find(
      (device) => device.id === (value ? value.id : null)
    );

    if (device) {
      updateFormState({
        devices: [
          ...formValues.devices,
          { location: location, device: device },
        ],
      });
      setLocation('');
    }
  };

  const handleSearchByChange = (searchBy: SearchBy) => {
    setSearchBy(searchBy);
    if (searchBy === 'GUID')
      setSearchOptions(inventory.map((device) => device.id));
    if (searchBy === 'MAC')
      setSearchOptions(inventory.map((device) => device.mac));
    if (searchBy === 'Serial')
      setSearchOptions(inventory.map((device) => device.serial));
  };

  const getOptionKey = (option: InventoryDevice) => {
    switch (searchBy) {
      case 'MAC':
        return option.mac;
      case 'Serial':
        return option.serial;
      case 'GUID':
      default:
        return option.id;
    }
  };

  return (
    <div className="w-full p-4">
      {asReview ? (
        formValues.devices.map((item, index) => (
          <ReviewItems
            key={`device-${index + 1}`}
            items={[
              {
                defaultValue: '',
                label: 'Location',
                required: false,
                value: item.location,
              },
              {
                defaultValue: 'N/A',
                label: 'Model',
                required: false,
                value: item.device.modelName,
              },
              {
                defaultValue: 'N/A',
                label: 'MAC Address',
                required: false,
                value: item.device.mac,
              },
              {
                defaultValue: 'N/A',
                label: 'Device ID',
                required: false,
                value: item.device.id,
              },
              {
                align: 'left',
                defaultValue: 'N/A',
                label: 'Serial',
                maxWidth: 150,
                noWrap: true,
                required: false,
                value: item.device.serial,
              },
            ]}
            itemOrientation="vertical"
          />
        ))
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-8 gap-4">
            <Label className="max-w-xs md:col-span-2" text="Location" required>
              <Input
                className="p-1"
                name="location"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setLocation(event.target.value)
                }
                required
                value={location}
              />
            </Label>

            <Label className="w-full md:col-span-6" text="Search Inventory">
              <DynamicSearchBar
                clearOnSelect
                disabled={location === ''}
                groupBy={(option: InventoryDevice) => option.modelName}
                loading={loading}
                loadingText="Loading Inventory..."
                optionLabel={(option: InventoryDevice) => getOptionKey(option)}
                options={inventory.filter(
                  (i) =>
                    !formValues.devices.find(
                      (d: CreateAccountDevice) => d.device.id === i.id
                    )
                )}
                onSearch={handleSearch}
                onSearchByChange={handleSearchByChange}
                resultLimit={5}
                searchFields={[
                  { label: 'Device ID', value: 'GUID' },
                  { label: 'MAC Address', value: 'MAC' },
                  { label: 'Serial Number', value: 'Serial' },
                ]}
                setSearchInUse={() => null}
              />
            </Label>
          </div>
          <DeviceList
            devices={formValues.devices}
            onRemove={(deviceId) =>
              updateFormState({
                devices: formValues.devices.filter(
                  (d: CreateAccountDevice) => d.device.id !== deviceId
                ),
              })
            }
          />
        </>
      )}
    </div>
  );
};
